// src/contexts/FlexiRequestContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useEmployees } from './EmployeeContext';
import { useAuth } from '../hooks/useAuth';
import flexiService from '../services/flexi.service';

const FlexiRequestContext = createContext();

export const useFlexiRequests = () => {
    const context = useContext(FlexiRequestContext);
    if (!context) {
        throw new Error('useFlexiRequests must be used within a FlexiRequestProvider');
    }
    return context;
};

export const FlexiRequestProvider = ({ children }) => {
    const { employees } = useEmployees();
    const { user } = useAuth();

    // Global state for all flexi component requests (now from backend)
    const [allFlexiRequests, setAllFlexiRequests] = useState([]);
    const [loading, setLoading] = useState(false);

    // Current user from auth context
    const currentUser = user;

    // Available users now comes from EmployeeContext - automatically synchronized
    const availableUsers = employees;

    // Fetch requests based on user role
    useEffect(() => {
        if (!user) return;

        const fetchRequests = async () => {
            try {
                setLoading(true);
                let response;

                // Fetch requests based on user role
                if (user.is_admin) {
                    response = await flexiService.getAllRequests();
                } else if (user.is_manager || user.reporting_manager_id) {
                    // If user is a manager, fetch team requests
                    // Otherwise fetch only own requests
                    const teamResponse = await flexiService.getTeamRequests();
                    const myResponse = await flexiService.getMyRequests();

                    // Combine and deduplicate
                    const combined = [...(myResponse.requests || []), ...(teamResponse.requests || [])];
                    const unique = Array.from(new Map(combined.map(item => [item.id, item])).values());
                    response = { requests: unique };
                } else {
                    response = await flexiService.getMyRequests();
                }

                setAllFlexiRequests(response.requests || []);
            } catch (error) {
                // Flexi requests API may not be available — fail silently
                if (error?.response?.status !== 404) {
                    console.warn('Flexi requests unavailable:', error?.response?.status || error.message);
                }
                setAllFlexiRequests([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRequests();
    }, [user]);

    // Add a new flexi request (now calls backend API)
    const addFlexiRequest = async (request) => {
        try {
            setLoading(true);
            const response = await flexiService.createRequest({
                componentName: request.title || request.component,
                componentType: request.componentType || 'allowance',
                amount: parseFloat(request.amount),
                currency: request.currency || 'INR',
                reason: request.reason,
                supportingDocumentUrl: request.supportingDocument?.url
            });

            // Refresh requests list
            const updatedResponse = await flexiService.getMyRequests();
            setAllFlexiRequests(updatedResponse.requests || []);

            return response.request;
        } catch (error) {
            console.error('Error adding flexi request:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // Update request status (for manager/admin actions) - now calls backend API
    const updateRequestStatus = async (requestId, status, comments) => {
        try {
            setLoading(true);

            if (status === 'approved' || status === 'Open') {
                await flexiService.approveRequest(requestId, comments);
            } else if (status === 'rejected' || status === 'Dismiss') {
                await flexiService.rejectRequest(requestId, comments || 'Rejected', comments);
            } else if (user?.is_admin) {
                await flexiService.updateRequestStatus(requestId, status, comments);
            }

            // Refresh requests list
            let response;
            if (user.is_admin) {
                response = await flexiService.getAllRequests();
            } else if (user.is_manager) {
                response = await flexiService.getTeamRequests();
            } else {
                response = await flexiService.getMyRequests();
            }
            setAllFlexiRequests(response.requests || []);
        } catch (error) {
            console.error('Error updating request status:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // Delete/Cancel a request - now calls backend API
    const deleteFlexiRequest = async (requestId, reason = '') => {
        try {
            setLoading(true);
            await flexiService.cancelRequest(requestId, reason || 'Cancelled by employee');

            // Refresh requests list
            const response = await flexiService.getMyRequests();
            setAllFlexiRequests(response.requests || []);
        } catch (error) {
            console.error('Error cancelling flexi request:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // Get requests for current employee
    const getEmployeeRequests = () => {
        return allFlexiRequests.filter(request => request.employee_id === currentUser?.id);
    };

    // Get all pending requests (for admin/manager view)
    const getPendingRequests = () => {
        return allFlexiRequests.filter(request =>
            request.status === 'pending' || request.status === 'under_review'
        );
    };

    // Get requests by status
    const getRequestsByStatus = (status) => {
        return allFlexiRequests.filter(request => request.status === status);
    };

    // Get requests for a specific employee (admin view)
    const getRequestsByEmployee = (employeeId) => {
        return allFlexiRequests.filter(request => request.employee_id === employeeId);
    };


    const value = {
        allFlexiRequests,
        currentUser,
        availableUsers,
        loading,
        addFlexiRequest,
        updateRequestStatus,
        deleteFlexiRequest,
        getEmployeeRequests,
        getPendingRequests,
        getRequestsByStatus,
        getRequestsByEmployee,
        // Add new method to manually refresh requests
        refreshRequests: async () => {
            if (!user) return;
            try {
                setLoading(true);
                let response;
                if (user.is_admin) {
                    response = await flexiService.getAllRequests();
                } else if (user.is_manager) {
                    response = await flexiService.getTeamRequests();
                } else {
                    response = await flexiService.getMyRequests();
                }
                setAllFlexiRequests(response.requests || []);
            } catch (error) {
                console.error('Error refreshing requests:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <FlexiRequestContext.Provider value={value}>
            {children}
        </FlexiRequestContext.Provider>
    );
};